import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getLogsWithAutoEval, getLogsAnalytics, getPrompts, getCurrentUser } from '../services/api';

const Logs = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [logs, setLogs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    page: 1,
    limit: 25,
    prompt_id: '',
    from_date: '',
    to_date: '',
    model: '',
    auto_eval_status: ''
  });

  useEffect(() => {
    loadUser();
  }, []);

  useEffect(() => {
    if (user?.organization_id) {
      loadLogs();
      loadAnalytics();
    }
  }, [user, filters]);

  useEffect(() => {
    if (projectId) {
      loadPrompts();
    }
  }, [projectId]);

  const loadUser = async () => {
    try {
      const userData = await getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Error loading user:', error);
    }
  };

  const loadLogs = async () => {
    if (!user?.organization_id) return;
    
    try {
      setLoading(true);
      const data = await getLogsWithAutoEval(user.organization_id, filters);
      setLogs(data.logs || []);
      setPagination(data.pagination || {});
    } catch (error) {
      console.error('Error loading logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    if (!user?.organization_id) return;

    try {
      setAnalyticsLoading(true);
      const data = await getLogsAnalytics(user.organization_id, 7);
      setAnalytics(data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadPrompts = async () => {
    if (!projectId) return;

    try {
      const promptsData = await getPrompts(projectId);
      setPrompts(promptsData);
    } catch (error) {
      console.error('Error loading prompts:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const formatDateTime = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const parseMetadata = (metadata) => {
    if (typeof metadata === 'string') {
      try {
        return JSON.parse(metadata);
      } catch {
        return {};
      }
    }
    return metadata || {};
  };

  // Icons for status indicators
  const CheckCircleIcon = () => (
    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  const XCircleIcon = () => (
    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  // Simple SVG icons to replace emojis
  const ChartIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );

  const MessageIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.959 8.959 0 01-4.906-1.451L3 21l2.451-5.094A8.959 8.959 0 013 12c0-4.418 3.582-8 8-8s8 3.582 8 8z" />
    </svg>
  );

  const LightningIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );

  const CalendarIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );

  const AnalyticsCard = ({ title, value, subtitle, iconType }) => (
    <div className="bg-white p-6 rounded-lg shadow-sm border">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-celadon-100 rounded-lg flex items-center justify-center">
            {iconType === 'chart' && <ChartIcon />}
            {iconType === 'message' && <MessageIcon />}
            {iconType === 'lightning' && <LightningIcon />}
            {iconType === 'calendar' && <CalendarIcon />}
          </div>
        </div>
        <div className="ml-4">
          <div className="text-sm font-medium text-gray-500">{title}</div>
          <div className="text-2xl font-bold text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{subtitle}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Organization Logs</h1>
                <p className="mt-1 text-sm text-gray-600">
                  Overview of all prompt usage across your organization
                </p>
              </div>
              <button
                onClick={() => navigate(projectId ? `/projects/${projectId}` : '/dashboard')}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                ← Back to {projectId ? 'Project' : 'Dashboard'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Analytics Cards */}
        {!analyticsLoading && analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <AnalyticsCard
              title="Total Logs"
              value={analytics.overview?.total_logs || 0}
              subtitle="Last 7 days"
              iconType="chart"
            />
            <AnalyticsCard
              title="Unique Prompts"
              value={analytics.overview?.unique_prompts || 0}
              subtitle="Different prompts used"
              iconType="message"
            />
            <AnalyticsCard
              title="Avg Response Time"
              value={formatDuration(analytics.overview?.avg_execution_time)}
              subtitle="Execution time"
              iconType="lightning"
            />
            <AnalyticsCard
              title="Active Days"
              value={analytics.overview?.active_days || 0}
              subtitle="Days with activity"
              iconType="calendar"
            />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-6 rounded-lg shadow-sm border mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prompt
              </label>
              <select
                value={filters.prompt_id}
                onChange={(e) => handleFilterChange('prompt_id', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">All Prompts</option>
                {prompts.map(prompt => (
                  <option key={prompt.id} value={prompt.id}>
                    {prompt.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Model
              </label>
              <input
                type="text"
                value={filters.model}
                onChange={(e) => handleFilterChange('model', e.target.value)}
                placeholder="e.g., gpt-4, claude-3"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={filters.from_date}
                onChange={(e) => handleFilterChange('from_date', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={filters.to_date}
                onChange={(e) => handleFilterChange('to_date', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Auto-eval Status
              </label>
              <select
                value={filters.auto_eval_status}
                onChange={(e) => handleFilterChange('auto_eval_status', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="pass">Pass</option>
                <option value="fail">Fail</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Per Page
              </label>
              <select
                value={filters.limit}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white shadow-sm border rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Execution Logs
              {pagination.total && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({pagination.total} total)
                </span>
              )}
            </h3>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-celadon-500"></div>
              <p className="mt-2 text-sm text-gray-600">Loading logs...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500 mb-2">No logs found. Start using the SDK to see logs here.</p>
              <p className="text-sm text-gray-400">
                Tip: You can also view logs for individual prompts by clicking on their names in the table above, 
                or by visiting the "Logs" tab on any prompt's detail page.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prompt
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Input
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Output
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Model
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Auto-eval
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Metadata
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log) => {
                    const metadata = parseMetadata(log.metadata);
                    return (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDateTime(log.logged_at)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div>
                            {log.prompt_name && log.prompt_id ? (
                              <div>
                                <Link 
                                  to={`/prompts/${log.prompt_id}/logs`}
                                  className="font-medium text-celadon-600 hover:text-celadon-800 hover:underline"
                                >
                                  {log.prompt_name}
                                </Link>
                                {log.version && (
                                  <div className="text-xs text-gray-500">{log.version}</div>
                                )}
                              </div>
                            ) : log.prompt_name ? (
                              <div>
                                <div className="font-medium">{log.prompt_name}</div>
                                {log.version && (
                                  <div className="text-xs text-gray-500">{log.version}</div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">Untracked prompt</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                          <div 
                            className="cursor-pointer hover:text-celadon-600"
                            title={log.input_text}
                          >
                            {truncateText(log.input_text, 80)}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                          <div 
                            className="cursor-pointer hover:text-celadon-600"
                            title={log.output_text}
                          >
                            {truncateText(log.output_text, 80)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.model || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDuration(log.execution_time_ms)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.auto_eval_status ? (
                            <div className="space-y-1">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                log.auto_eval_status === 'pass' ? 'badge-status-success' :
                                log.auto_eval_status === 'fail' ? 'badge-status-error' :
                                log.auto_eval_status === 'pending' ? 'badge-status-info' :
                                'badge-status-info'
                              }`}>
                                {log.auto_eval_status === 'pass' ? 'Pass' : 
                                 log.auto_eval_status === 'fail' ? 'Fail' : 
                                 log.auto_eval_status === 'pending' ? 'Pending' : 
                                 log.auto_eval_status}
                              </span>
                              {log.auto_eval_status === 'pass' && log.auto_eval_avg_score !== null && (
                                <div className="text-xs text-gray-500">
                                  Score: {(log.auto_eval_avg_score * 100).toFixed(0)}%
                                  {log.auto_eval_pass_count > 0 ? (
                                    <span className="text-celadon-600 ml-1 inline-flex items-center">
                                      <CheckCircleIcon />
                                      <span className="ml-1">Pass</span>
                                    </span>
                                  ) : (
                                    <span className="text-gray-600 ml-1 inline-flex items-center">
                                      <XCircleIcon />
                                      <span className="ml-1">Fail</span>
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {Object.keys(metadata).length > 0 ? (
                            <div className="space-y-1">
                              {Object.entries(metadata).slice(0, 2).map(([key, value]) => (
                                <div key={key} className="text-xs">
                                  <span className="font-medium">{key}:</span> {String(value)}
                                </div>
                              ))}
                              {Object.keys(metadata).length > 2 && (
                                <div className="text-xs text-gray-500">
                                  +{Object.keys(metadata).length - 2} more
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Page {pagination.page} of {pagination.totalPages}
                  {pagination.total && (
                    <span> ({pagination.total} total logs)</span>
                  )}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Logs; 